/**
 * ============================================================================
 * ClinIA Perú - Configuración: firma, hoja membretada, Gmail y guías
 * ============================================================================
 * Tres tarjetas independientes del resto de Configuración, cada una con su
 * propio guardado:
 *   1. Firma y hoja membretada (imágenes que usa el PDF de las guías)
 *   2. Gmail del médico para enviar correos a sus pacientes
 *   3. Mis guías nutricionales (crear, editar, archivar)
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Apple, Image, Loader2, Mail, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import api from '../../lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const errorText = (error, fallback) => error?.response?.data?.detail || fallback;

const readImage = (file, maxMb) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error(''));
  if (!['image/png', 'image/jpeg'].includes(file.type)) return reject(new Error('Solo se permiten imágenes PNG o JPG'));
  if (file.size > maxMb * 1024 * 1024) return reject(new Error(`La imagen no debe superar ${maxMb}MB`));
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.readAsDataURL(file);
});

// ==================== 1. FIRMA Y HOJA MEMBRETADA ====================

function ImagesCard({ initial }) {
  const [signature, setSignature] = useState(initial.signature_image || null);
  const [letterhead, setLetterhead] = useState(initial.letterhead_image || null);
  const [saving, setSaving] = useState(false);

  const pick = (setter, maxMb) => async (e) => {
    try {
      setter(await readImage(e.target.files[0], maxMb));
    } catch (err) {
      if (err.message) toast.error(err.message);
    }
    e.target.value = '';
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/doctors/my-branding', { signature_image: signature || '', letterhead_image: letterhead || '' });
      toast.success('Firma y hoja membretada guardadas');
    } catch (error) {
      toast.error(errorText(error, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const slot = (title, hint, value, setter, maxMb, previewClass) => (
    <div className="space-y-2">
      <Label>{title}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="border-2 border-dashed rounded-lg p-3 flex items-center justify-center bg-gray-50 min-h-[140px]">
        {value ? <img src={value} alt={title} className={previewClass} /> : <span className="text-sm text-gray-400">Sin imagen</span>}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" asChild>
          <label className="cursor-pointer">
            <Image className="w-4 h-4 mr-1" /> {value ? 'Cambiar' : 'Subir'}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick(setter, maxMb)} />
          </label>
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setter(null)}>
            <Trash2 className="w-4 h-4 mr-1" /> Quitar
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-1">Firma y hoja membretada</h2>
      <p className="text-sm text-muted-foreground mb-4">Se usan en el PDF de las guías nutricionales.</p>
      <div className="grid md:grid-cols-2 gap-6">
        {slot('Firma', 'PNG con fondo transparente o blanco. Máx. 1.5MB.', signature, setSignature, 1.5, 'max-h-24')}
        {slot('Hoja membretada', 'Imagen A4 vertical con su logo, datos y pie. Se pone de fondo en cada página. Máx. 3MB.',
          letterhead, setLetterhead, 3, 'max-h-56 shadow')}
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar imágenes
        </Button>
      </div>
    </Card>
  );
}

// ==================== 2. GMAIL ====================

function GmailCard() {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/doctors/my-email').then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      await api.put('/doctors/my-email', { email, app_password: password });
      toast.success('Gmail conectado. Los correos a sus pacientes saldrán desde su cuenta');
      setPassword('');
      load();
    } catch (error) {
      toast.error(errorText(error, 'No se pudo conectar'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.delete('/doctors/my-email');
    toast.success('Gmail desconectado');
    load();
  };

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Mail className="w-5 h-5" /> Correo para enviar a pacientes</h2>
      {status?.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
          <p className="text-sm text-green-800">Conectado: <strong>{status.email}</strong>. Los correos salen desde esta cuenta.</p>
          <Button variant="outline" size="sm" onClick={disconnect}>Desconectar</Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3">
            Conecte su <strong>Gmail</strong> para que sus pacientes reciban los correos a su nombre.
            {status?.system_email && <> Mientras no lo conecte, se envían desde <strong>{status.system_email}</strong> y las respuestas le llegan a usted.</>}
          </p>
          <ol className="text-sm list-decimal pl-5 space-y-1 mb-4 text-gray-700">
            <li>Active la <a className="text-blue-600 underline" href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noreferrer">verificación en dos pasos</a> de su cuenta Google (si ya la tiene, siga al paso 2).</li>
            <li>Entre a <a className="text-blue-600 underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">Contraseñas de aplicaciones</a>, escriba "ClinIA" y pulse <em>Crear</em>.</li>
            <li>Copie la clave de 16 letras que aparece y péguela aquí abajo.</li>
          </ol>
          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor="gmail">Su Gmail</Label>
              <Input id="gmail" type="email" placeholder="doctor@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="apppass">Contraseña de aplicación</Label>
              <Input id="apppass" placeholder="abcd efgh ijkl mnop" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
            </div>
            <Button onClick={connect} disabled={busy || !email || !password}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Conectar
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

// ==================== 3. GUÍAS NUTRICIONALES ====================

const MEAL_TIMES = ['Desayuno', 'Media mañana', 'Almuerzo', 'Media tarde', 'Cena', 'Colación nocturna'];

const emptyGuide = () => ({
  title: '',
  summary: '',
  content: {
    intro: '', benefits: [], recommendations: [{ title: '', detail: '' }],
    foods: [{ group: '', allowed: '', avoid: '' }], menu_features: [],
    menus: [{ title: 'Opción 1', meals: MEAL_TIMES.map((time) => ({ time, food: '' })) }], notes: '',
  },
});

// Listas simples (beneficios, características del menú): una por línea
const toLines = (list) => (list || []).join('\n');
const fromLines = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);

function GuideEditor({ value, onCancel, onSaved }) {
  const [g, setG] = useState(value);
  const [saving, setSaving] = useState(false);
  const c = g.content;
  const setC = (patch) => setG({ ...g, content: { ...c, ...patch } });
  const setItem = (key, i, patch) => setC({ [key]: c[key].map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const removeItem = (key, i) => setC({ [key]: c[key].filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = g.id
        ? await api.put(`/nutrition-guides/${g.id}`, g)
        : await api.post('/nutrition-guides', g);
      toast.success('Guía guardada');
      onSaved(data);
    } catch (error) {
      toast.error(errorText(error, 'Error al guardar la guía'));
    } finally {
      setSaving(false);
    }
  };

  const RemoveBtn = ({ onClick }) => (
    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClick}><X className="w-4 h-4" /></Button>
  );

  return (
    <div className="space-y-5 border rounded-lg p-4 bg-white">
      <div>
        <Label>Título</Label>
        <Input value={g.title} onChange={(e) => setG({ ...g, title: e.target.value })} placeholder="Recomendaciones nutricionales para…" />
      </div>
      <div>
        <Label>Resumen corto para la receta</Label>
        <Textarea rows={2} value={g.summary} onChange={(e) => setG({ ...g, summary: e.target.value })}
          placeholder="Lo que se inserta en 'Indicaciones Nutricionales' con un clic" />
      </div>
      <div>
        <Label>Introducción</Label>
        <Textarea rows={3} value={c.intro} onChange={(e) => setC({ intro: e.target.value })} />
      </div>
      <div>
        <Label>Beneficios (uno por línea)</Label>
        <Textarea rows={3} value={toLines(c.benefits)} onChange={(e) => setC({ benefits: fromLines(e.target.value) })} />
      </div>

      <div className="space-y-2">
        <Label>Recomendaciones</Label>
        {c.recommendations.map((r, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Input className="w-1/3" placeholder="Título" value={r.title} onChange={(e) => setItem('recommendations', i, { title: e.target.value })} />
            <Textarea rows={2} className="flex-1" placeholder="Explicación" value={r.detail} onChange={(e) => setItem('recommendations', i, { detail: e.target.value })} />
            <RemoveBtn onClick={() => removeItem('recommendations', i)} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setC({ recommendations: [...c.recommendations, { title: '', detail: '' }] })}>
          <Plus className="w-4 h-4 mr-1" /> Recomendación
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Alimentos permitidos y no permitidos</Label>
        {c.foods.map((f, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Input className="w-1/5" placeholder="Grupo" value={f.group} onChange={(e) => setItem('foods', i, { group: e.target.value })} />
            <Textarea rows={2} className="flex-1" placeholder="Permitidos" value={f.allowed} onChange={(e) => setItem('foods', i, { allowed: e.target.value })} />
            <Textarea rows={2} className="flex-1" placeholder="Evitar" value={f.avoid} onChange={(e) => setItem('foods', i, { avoid: e.target.value })} />
            <RemoveBtn onClick={() => removeItem('foods', i)} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setC({ foods: [...c.foods, { group: '', allowed: '', avoid: '' }] })}>
          <Plus className="w-4 h-4 mr-1" /> Grupo de alimentos
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Menús de ejemplo</Label>
        <Textarea rows={2} placeholder="Características del menú (una por línea, opcional)" value={toLines(c.menu_features)}
          onChange={(e) => setC({ menu_features: fromLines(e.target.value) })} />
        {c.menus.map((m, i) => (
          <div key={i} className="border rounded-md p-3 space-y-2">
            <div className="flex gap-2">
              <Input className="w-1/3 font-medium" value={m.title} onChange={(e) => setItem('menus', i, { title: e.target.value })} />
              <div className="flex-1" />
              <RemoveBtn onClick={() => removeItem('menus', i)} />
            </div>
            {m.meals.map((meal, k) => (
              <div key={k} className="flex gap-2">
                <Input className="w-1/4 text-sm" value={meal.time} onChange={(e) => setItem('menus', i, { meals: m.meals.map((x, y) => (y === k ? { ...x, time: e.target.value } : x)) })} />
                <Input className="flex-1 text-sm" value={meal.food} onChange={(e) => setItem('menus', i, { meals: m.meals.map((x, y) => (y === k ? { ...x, food: e.target.value } : x)) })} />
              </div>
            ))}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm"
          onClick={() => setC({ menus: [...c.menus, { title: `Opción ${c.menus.length + 1}`, meals: MEAL_TIMES.map((time) => ({ time, food: '' })) }] })}>
          <Plus className="w-4 h-4 mr-1" /> Menú
        </Button>
      </div>

      <div>
        <Label>Nota final (opcional)</Label>
        <Textarea rows={2} value={c.notes} onChange={(e) => setC({ notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={save} disabled={saving || g.title.trim().length < 3}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar guía
        </Button>
      </div>
    </div>
  );
}

function GuidesCard() {
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/nutrition-guides').then(({ data }) => setGuides(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const archive = async (guide) => {
    if (!window.confirm(`¿Archivar "${guide.title}"? Ya no aparecerá en las consultas.`)) return;
    await api.delete(`/nutrition-guides/${guide.id}`);
    toast.success('Guía archivada');
    load();
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Apple className="w-5 h-5 text-green-600" /> Mis guías nutricionales</h2>
        {!editing && <Button size="sm" onClick={() => setEditing(emptyGuide())}><Plus className="w-4 h-4 mr-1" /> Nueva guía</Button>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Aparecen en la consulta, debajo de Indicaciones Nutricionales, para insertar el resumen, imprimir el PDF o enviarlas por correo.
      </p>

      {editing ? (
        <GuideEditor value={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : guides.length === 0 ? (
        <p className="text-sm text-gray-500">Aún no tiene guías.</p>
      ) : (
        <div className="divide-y border rounded-lg">
          {guides.map((g) => (
            <div key={g.id} className="flex items-center justify-between p-3">
              <div>
                <p className="font-medium">{g.title}</p>
                <p className="text-xs text-muted-foreground">
                  {(g.content?.recommendations || []).length} recomendaciones · {(g.content?.menus || []).length} menús
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditing(JSON.parse(JSON.stringify(g)))}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => archive(g)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function DoctorDocumentsSettings({ doctorData }) {
  if (!doctorData) return null;
  return (
    <>
      <ImagesCard initial={doctorData} />
      <GmailCard />
      <GuidesCard />
    </>
  );
}
